import { ReactElement, useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import {
  Contract,
  ContractItem,
  ContractItemPriceRaiseStatus,
  getContractItemPriceRaiseDto,
  useRejectContractItemPriceRaiseMutation,
  useUpdateContractItemPriceRaiseMutation,
} from 'entities/Contract';
import { RejectPriceRaiseModal } from '../../ui/RejectPriceRaiseModal';

export interface OpenRejectPriceRaiseParams {
  contract: Contract;
  newPrice: number;
  oldPrice?: number;
  contractItem?: Nullable<ContractItem>;
  contractItemPriceRaiseId?: string;
}

interface HookApi {
  openRejectPriceRaise: (params: OpenRejectPriceRaiseParams) => void;
  rejectPriceRaiseModal: ReactElement;
}

export const useRejectPriceRaise = (): HookApi => {
  const { t } = useAppTranslation(['contracts', 'common']);

  const [params, setParams] = useState<Nullable<OpenRejectPriceRaiseParams>>(null);
  const [rejectPriceRaise, { isLoading: isCreating }] = useRejectContractItemPriceRaiseMutation();
  const [updatePriceRaise, { isLoading: isUpdating }] = useUpdateContractItemPriceRaiseMutation();

  const openRejectPriceRaise = useCallback((nextParams: OpenRejectPriceRaiseParams): void => {
    setParams(nextParams);
  }, []);

  const closeRejectPriceRaise = useCallback((): void => {
    setParams(null);
  }, []);

  const confirmRejectPriceRaise = useCallback(
    async (months: number): Promise<void> => {
      if (!params) {
        return;
      }

      const dto = getContractItemPriceRaiseDto(params.contract, params.newPrice, params.oldPrice, params.contractItem);

      try {
        if (params.contractItemPriceRaiseId) {
          await updatePriceRaise({
            contractItemPriceRaiseId: params.contractItemPriceRaiseId,
            ...dto,
            status: ContractItemPriceRaiseStatus.REJECTED,
            months,
          }).unwrap();
        } else {
          await rejectPriceRaise({
            contractId: params.contract.contractId,
            months,
            ...dto,
          }).unwrap();
        }

        showNotification('info', t('Success', { ns: 'common' }), t('Price raise has been rejected'));
        closeRejectPriceRaise();
      } catch {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when rejecting price raise'));
      }
    },
    [closeRejectPriceRaise, params, rejectPriceRaise, t, updatePriceRaise],
  );

  return {
    openRejectPriceRaise,
    rejectPriceRaiseModal: (
      <RejectPriceRaiseModal
        isOpened={Boolean(params)}
        contract={params?.contract || null}
        isLoading={isCreating || isUpdating}
        onCancel={closeRejectPriceRaise}
        onConfirm={confirmRejectPriceRaise}
      />
    ),
  };
};
